'use client';

import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useGetLotAnimalEbvsQuery } from '@/publicSearch/graphql/queries.generated';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import {
  FieldOption,
  LogicalOperator,
  SortDirection,
} from '@/components/publicSearch/constants';
import { parsePublicSearchUrlState } from '@/lib/parsePublicSearchUrlState';
import { buildLotAnimalFilterGroups } from '@/hooks/publicSearch/sale-catalogue/buildLotAnimalFilterGroups';
import { isUnmappedI6LookupError } from '@/components/publicSearch/search/expandI6LookupFilters';
import type {
  LotAnimalEbvSortField,
  SortDirectionOrder,
} from '@/graphqlClient/types';
import { resolveSaleLotNavigationState } from '@/hooks/publicSearch/sale-catalogue/saleLotNavigationUtils';

const NAVIGATION_PAGE_SIZE = 1000;

type UseSaleLotNavigationParams = {
  saleId: string;
  lotNo: string;
  animalId: string;
};

export function useSaleLotNavigation({
  saleId,
  lotNo,
  animalId,
}: UseSaleLotNavigationParams) {
  const searchParams = useSearchParams();
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const societySettings = usePublicSearchContext(
    (context) => context.society.settings,
  );

  const parsedUrlState = useMemo(
    () => parsePublicSearchUrlState(searchParams),
    [searchParams],
  );

  // null = skip query. Never fall back to [] — that drops saleId scoping.
  // Bail before filter construction when catalogue context is incomplete so
  // lookup-marker expansion cannot throw on ordinary animal-detail pages.
  const filterGroups = useMemo(() => {
    if (!saleId || !lotNo) return null;
    try {
      return buildLotAnimalFilterGroups({
        saleId,
        filters: parsedUrlState?.filters ?? [],
        commonFilterOperator:
          parsedUrlState?.commonFilterOperator ?? LogicalOperator.and,
        settings: societySettings,
      });
    } catch (error) {
      if (isUnmappedI6LookupError(error)) return null;
      throw error;
    }
  }, [parsedUrlState, saleId, lotNo, societySettings]);

  const sortOptions = useMemo(() => {
    const validSortFields = new Set<string>([
      FieldOption.lotNo.value,
      FieldOption.animalName.value,
      FieldOption.animalIdent.value,
      FieldOption.dateOfBirth.value,
      FieldOption.registrationStatus.value,
      FieldOption.sex.value,
    ]);
    for (let i = 1; i <= 26; i++) {
      validSortFields.add(`EBV${i}`);
    }

    const fromUrl = (parsedUrlState?.gridSortModel ?? [])
      .filter(({ field, sort }) => field && sort && validSortFields.has(field))
      .map(({ field, sort }) => {
        const isEbvValueSort = /^EBV\d+$/.test(field);
        return {
          direction: SortDirection[sort!] as SortDirectionOrder,
          field: field as LotAnimalEbvSortField,
          nullsLast: isEbvValueSort,
        };
      });

    if (fromUrl.length > 0) return fromUrl;

    return [
      {
        field: FieldOption.lotNo.value as LotAnimalEbvSortField,
        direction: SortDirection.asc as SortDirectionOrder,
        nullsLast: false,
      },
    ];
  }, [parsedUrlState?.gridSortModel]);

  const { data, loading } = useGetLotAnimalEbvsQuery({
    skip: !orgId || !saleId || !lotNo || !animalId || !filterGroups,
    variables: {
      requestInput: {
        pagination: {
          currentPage: 1,
          pageSize: NAVIGATION_PAGE_SIZE,
        },
        // Placeholder only when skip=true; successful builds always include saleId.
        filterGroups: filterGroups ?? [],
        sortOptions,
      },
    },
    context: {
      headers: setDefaultHeader(orgId ?? ''),
    },
    fetchPolicy: APOLLO_FETCH_POLICY.NO_CACHE,
  });

  const items = useMemo(
    () => data?.lotAnimalEbvs?.items ?? [],
    [data?.lotAnimalEbvs?.items],
  );
  const totalCount = data?.lotAnimalEbvs?.totalCount ?? items.length;

  const navigation = useMemo(
    () => resolveSaleLotNavigationState(items, animalId, lotNo, totalCount),
    [animalId, items, lotNo, totalCount],
  );

  return {
    loading,
    ...navigation,
  };
}
